import React from "react";
import { Twitter, Facebook, Linkedin, Mail, Phone } from "lucide-react";
import { SITE_CONFIG, CONTACT } from "../../data/mock";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: "Features", href: "#features" },
      { label: "Pricing", href: "#pricing" },
      { label: "Testimonials", href: "#testimonials" },
      { label: "FAQ", href: "#faq" },
    ],
    company: [
      { label: "About Us", href: "#" },
      { label: "Blog", href: "#" },
      { label: "Careers", href: "#" },
      { label: "Contact", href: "#contact" },
    ],
    resources: [
      { label: "Documentation", href: "#" },
      { label: "Help Center", href: "#" },
      { label: "API Reference", href: "#" },
      { label: "System Status", href: "#" },
    ],
    legal: [
      { label: "Privacy Policy", href: "#" },
      { label: "Terms of Service", href: "#" },
      { label: "Cookie Policy", href: "#" },
      { label: "Data Protection", href: "#" },
    ],
  };

  const socialLinks = [
    { icon: Twitter, href: CONTACT.social.twitter, label: "Twitter" },
    { icon: Facebook, href: CONTACT.social.facebook, label: "Facebook" },
    { icon: Linkedin, href: CONTACT.social.linkedin, label: "LinkedIn" },
  ];

  return (
    <footer className="bg-[var(--bg-card)] border-t border-[var(--border-light)] transition-theme">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2">
            <a href="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                <img
                  src="logo.svg"
                  className="w-8 h-8 rounded-xl"
                  alt="Site Logo"
                />
              </div>
              <span className="font-bold text-xl text-[var(--text-primary)]">
                {SITE_CONFIG.name}
              </span>
            </a>
            <p className="text-[var(--text-body)] opacity-85 mb-6 leading-relaxed">
              {SITE_CONFIG.tagline}. Trusted by over 500 schools across Kenya.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-2 text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
              >
                <Mail className="w-4 h-4" />
                {CONTACT.email}
              </a>
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-2 text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
              >
                <Phone className="w-4 h-4" />
                {CONTACT.phone}
              </a>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-[var(--bg-section)] border border-[var(--border-light)] flex items-center justify-center hover:bg-[var(--accent-wash)] hover:border-[var(--accent-primary)] transition-all duration-200 group"
                >
                  <social.icon className="w-4 h-4 text-[var(--text-body)] opacity-75 group-hover:text-[var(--accent-text)] transition-colors group-hover:opacity-100" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-4">
              Product
            </h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-4">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-[var(--text-primary)] mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-sm text-[var(--text-body)] opacity-85 hover:text-[var(--accent-text)] transition-colors hover:opacity-100"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[var(--border-light)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-[var(--text-secondary)] font-medium">
              © {currentYear} {SITE_CONFIG.name}. All rights reserved.
            </p>
            <p className="text-sm text-[var(--text-secondary)] font-medium">
              Made with ❤️ for Kenyan schools
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
